// client/src/pages/admin/AdminCitiesManagement.js
import React, { useState, useEffect } from 'react';
import { cityAPI } from '../../services/api';
import { toast } from 'react-toastify';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';

const AdminCitiesManagement = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    region: '',
    order: 0,
    active: true
  });

  useEffect(() => {
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      setLoading(true);
      const response = await cityAPI.getAll({ active: 'all', limit: 200 });
      setCities(response.data.data || []);
    } catch (error) {
      console.error('Error loading cities:', error);
      toast.error('Erreur lors du chargement des villes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCity) {
        await cityAPI.update(editingCity._id, formData);
        toast.success('Ville mise à jour avec succès');
      } else {
        await cityAPI.create(formData);
        toast.success('Ville créée avec succès');
      }

      setShowModal(false);
      setEditingCity(null);
      setFormData({ name: '', region: '', order: 0, active: true });
      loadCities();
    } catch (error) {
      console.error('Error saving city:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (city) => {
    setEditingCity(city);
    setFormData({
      name: city.name,
      region: city.region || '',
      order: city.order || 0,
      active: city.active
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      await cityAPI.delete(deleteTarget._id);
      toast.success('Ville supprimée avec succès');
      setDeleteTarget(null);
      loadCities();
    } catch (error) {
      console.error('Error deleting city:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleToggle = async (city) => {
    try {
      await cityAPI.toggle(city._id);
      toast.success(`Ville ${city.active ? 'désactivée' : 'activée'}`);
      loadCities();
    } catch (error) {
      console.error('Error toggling city:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Gestion des Villes</h1>
        <button
          onClick={() => {
            setEditingCity(null);
            setFormData({ name: '', region: '', order: 0, active: true });
            setShowModal(true);
          }}
          className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition"
        >
          <i className="fas fa-plus mr-2"></i>
          Ajouter une ville
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Ville
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Région
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Ordre
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cities.map((city) => (
              <tr key={city._id} className={!city.active ? 'bg-gray-50 opacity-60' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <i className="fas fa-map-marker-alt text-primary-600 mr-3"></i>
                    <div>
                      <div className="text-sm font-medium text-gray-900">{city.name}</div>
                      <div className="text-xs text-gray-500">{city.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {city.region || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  {city.order}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    city.active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {city.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => handleToggle(city)}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                    title={city.active ? 'Désactiver' : 'Activer'}
                  >
                    <i className={`fas fa-${city.active ? 'eye-slash' : 'eye'}`}></i>
                  </button>
                  <button
                    onClick={() => handleEdit(city)}
                    className="text-indigo-600 hover:text-indigo-900 mr-3"
                    title="Modifier"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    onClick={() => setDeleteTarget(city)}
                    className="text-red-600 hover:text-red-900"
                    title="Supprimer"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {cities.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            Aucune ville trouvée. Créez-en une pour commencer.
          </div>
        )}
      </div>

      {/* Modal Create/Edit */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6">
              {editingCity ? 'Modifier la ville' : 'Nouvelle ville'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Nom de la ville *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-300 rounded px-4 py-2"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Région</label>
                <input
                  type="text"
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  className="w-full border border-gray-300 rounded px-4 py-2"
                  placeholder="Ex: Tanger-Tétouan-Al Hoceïma"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2">Ordre d'affichage</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded px-4 py-2"
                  min="0"
                />
              </div>

              <div className="mb-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-gray-700">Ville active</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingCity(null);
                  }}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingCity ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <ConfirmDeleteModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          title="Supprimer la ville"
          message={`Êtes-vous sûr de vouloir supprimer "${deleteTarget.name}" ?`}
        />
      )}
    </div>
  );
};

export default AdminCitiesManagement;